// mds-dojo — dijit/Menu — FAITHFUL port of references/dijit/Menu.js.
// A context menu you can assign to multiple DOM nodes (right-click / Shift-F10 /
// leftClickToOpen). Subclass of DropDownMenu. declare from the faithful runtime.
//
// mds ADAPTATIONS (behavior-preserving):
//  - `require` + the has("dijit-legacy-requires") ready() preload block removed
//    (no AMD loader under Vite); `dojo/ready`/`require` imports dropped.
//  - `dojo/sniff` → the has("ie")/has("quirks") branches (all false on evergreen) are
//    collapsed to their non-IE result; `dojo/has` import then unused → dropped.
//  - dojo `on.selector(selector, evt)` (only used when this.selector is set) → the mds
//    onSelector() shim; the default (no selector) path stays plain `on(cn, evt, fn)`.
//  - `domGeometry.docScroll` (absent on mds) → winUtils.getScroll; the iframe-local
//    variable named `window` is renamed `iframeWin` to avoid shadowing the global.
//  - `_onBlur` instance-override uses the strict-mode explicit-caller inherited form
//    (this.inherited(name, caller, arguments)) required by the faithful declare runtime
//    for raw (undecorated) functions. `document.frames` (IE-only) cast to any.
import array from '../dojo/_base/array'; // array.forEach
import declare from '../dojo/_base/declare-runtime'; // declare
import dom from '../dojo/dom'; // dom.byId dom.isDescendant
import domAttr from '../dojo/dom-attr'; // domAttr.get domAttr.set domAttr.has domAttr.remove
import domGeometry from '../dojo/dom-geometry'; // domGeometry.position
import domStyle from '../dojo/dom-style'; // domStyle.getComputedStyle
import keys from '../dojo/keys'; // keys.F10
import lang from '../dojo/_base/lang'; // lang.hitch
import on from '../dojo/on';
import win from '../dojo/_base/window'; // win.body
import winUtils from '../dojo/window'; // winUtils.get
import pm from './popup';
import DropDownMenu from './DropDownMenu';
import { onSelector } from './_onSelector';

export default declare('dijit.Menu', DropDownMenu, {
  // summary:
  //		A context menu you can assign to multiple elements

  constructor: function (/*===== params, srcNodeRef =====*/) {
    this._bindings = [];
  },

  // targetNodeIds: [const] String[]
  //		Array of dom node ids of nodes to attach to.
  targetNodeIds: [],

  // selector: String?
  //		CSS expression to apply this Menu to descendants of targetNodeIds, rather than to
  //		the nodes specified by targetNodeIds themselves.
  selector: '',

  // contextMenuForWindow: [const] Boolean
  //		If true, right clicking anywhere on the window will cause this context menu to open.
  contextMenuForWindow: false,

  // leftClickToOpen: [const] Boolean
  //		If true, menu will open on left click instead of right click, similar to a file menu.
  leftClickToOpen: false,

  // refocus: Boolean
  //		When this menu closes, re-focus the element which had focus before it was opened.
  refocus: true,

  postCreate: function () {
    if (this.contextMenuForWindow) {
      this.bindDomNode(this.ownerDocumentBody);
    } else {
      array.forEach(this.targetNodeIds, this.bindDomNode, this);
    }
    this.inherited(arguments);
  },

  // thanks burstlib!
  _iframeContentWindow: function (/* HTMLIFrameElement */iframe_el: any) {
    // Returns the window reference of the passed iframe
    return winUtils.get(this._iframeContentDocument(iframe_el)) ||
      // Moz. TODO: is this available when defaultView isn't?
      this._iframeContentDocument(iframe_el)['__parent__'] ||
      (iframe_el.name && (document as any).frames[iframe_el.name]) || null;	//	Window
  },

  _iframeContentDocument: function (/* HTMLIFrameElement */iframe_el: any) {
    // Returns a reference to the document object inside iframe_el
    return iframe_el.contentDocument // W3
      || (iframe_el.contentWindow && iframe_el.contentWindow.document) // IE
      || (iframe_el.name && (document as any).frames[iframe_el.name] && (document as any).frames[iframe_el.name].document)
      || null;	//	HTMLDocument
  },

  bindDomNode: function (/*String|DomNode*/ node: any) {
    // Attach menu to given node
    node = dom.byId(node, this.ownerDocument);

    var cn;	// Connect node
    var iframe: any;

    // Support context menus on iframes.  Rather than binding to the iframe itself we need
    // to bind to the <body> node inside the iframe.
    if (node.tagName.toLowerCase() == 'iframe') {
      iframe = node;
      var iframeWin = this._iframeContentWindow(iframe);
      cn = win.body(iframeWin.document);
    } else {
      // To capture these events at the top level, attach to <html>, not <body>.
      cn = (node == win.body(this.ownerDocument) ? this.ownerDocument.documentElement : node);
    }

    // "binding" is the object to track our connection to the node
    var binding: any = {
      node: node,
      iframe: iframe,
    };

    // Save info about binding in _bindings[], and make node itself record index(+1) into
    // _bindings[] array.
    domAttr.set(node, '_dijitMenu' + this.id, this._bindings.push(binding));

    // Setup the connections to monitor click etc.
    var doConnects = lang.hitch(this, function (this: any, cn: any) {
      var selector = this.selector,
        self = this;
      // mds adaptation of dojo's delegatedEvent(): plain `on` unless a selector is set.
      function bind(eventType: any, listener: any): any {
        return selector ? onSelector(cn, selector, eventType, listener) : on(cn, eventType, listener);
      }
      return [
        bind(this.leftClickToOpen ? 'click' : 'contextmenu', function (this: any, evt: any) {
          evt.stopPropagation();
          evt.preventDefault();

          if ((new Date()).getTime() < self._lastKeyDown + 500) {
            // Ignore contextmenu/click events that were already processed in keydown handler below.
            return;
          }

          // Schedule context menu to be opened.
          self._scheduleOpen(this, iframe, { x: evt.pageX, y: evt.pageY }, evt.target);
        }),
        bind('keydown', function (this: any, evt: any) {
          if (evt.keyCode == 93 ||									// context menu key
            (evt.shiftKey && evt.keyCode == keys.F10) ||		// shift-F10
            (self.leftClickToOpen && evt.keyCode == keys.SPACE)	// space key
          ) {
            evt.stopPropagation();
            evt.preventDefault();

            // Open the menu around evt.target.
            self._scheduleOpen(this, iframe, null, evt.target);	// no coords - open near evt.target

            self._lastKeyDown = (new Date()).getTime();
          }
        }),
      ];
    });
    binding.connects = cn ? doConnects(cn) : [];

    if (iframe) {
      // Setup handler to [re]bind to the iframe when the contents are initially loaded,
      // and every time the contents change.
      binding.onloadHandler = lang.hitch(this, function (this: any) {
        var iframeWin = this._iframeContentWindow(iframe),
          cn = win.body(iframeWin.document);
        binding.connects = doConnects(cn);
      });
      if (iframe.addEventListener) {
        iframe.addEventListener('load', binding.onloadHandler, false);
      } else {
        iframe.attachEvent('onload', binding.onloadHandler);
      }
    }
  },

  unBindDomNode: function (/*String|DomNode*/ nodeName: any) {
    // Detach menu from given node
    var node;
    try {
      node = dom.byId(nodeName, this.ownerDocument);
    } catch (e) {
      // On IE the dom.byId() call will get an exception if the attach point was
      // the <body> node of an <iframe> that has since been reloaded.
      return;
    }

    // node["_dijitMenu" + this.id] contains index(+1) into my _bindings[] array
    var attrName = '_dijitMenu' + this.id;
    if (node && domAttr.has(node, attrName)) {
      var bid = domAttr.get(node, attrName) - 1, b = this._bindings[bid], h;
      while ((h = b.connects.pop())) {
        h.remove();
      }

      // Remove listener for iframe onload events
      var iframe = b.iframe;
      if (iframe) {
        if (iframe.removeEventListener) {
          iframe.removeEventListener('load', b.onloadHandler, false);
        } else {
          iframe.detachEvent('onload', b.onloadHandler);
        }
      }

      domAttr.remove(node, attrName);
      delete this._bindings[bid];
    }
  },

  _scheduleOpen: function (delegatedTarget: any, iframe: any, coords: any, target: any) {
    // Set timer to display myself.  Using a timer rather than displaying immediately solves
    // an IE problem where focus work in "open" causes the system context menu to appear.

    if (!this._openTimer) {
      this._openTimer = this.defer(function (this: any) {
        delete this._openTimer;
        this._openMyself({
          target: target,
          delegatedTarget: delegatedTarget,
          iframe: iframe,
          coords: coords,
        });
      }, 1);
    }
  },

  _openMyself: function (args: any) {
    // Internal function for opening myself when the user does a right-click or something similar.

    var target = args.target,
      iframe = args.iframe,
      coords = args.coords,
      byKeyboard = !coords;

    // To be used by MenuItem event handlers to tell which node the menu was opened on
    this.currentTarget = args.delegatedTarget;

    // Get coordinates to open menu, either at specified (mouse) position or (if triggered via keyboard)
    // then near the node the menu is assigned to.
    if (coords) {
      if (iframe) {
        // Specified coordinates are on <body> node of an <iframe>, convert to match main document
        var ifc = (domGeometry as any).position(iframe, true),
          iframeWin = this._iframeContentWindow(iframe),
          scroll = winUtils.getScroll(iframeWin.document);

        var cs = domStyle.getComputedStyle(iframe),
          tp = domStyle.toPixelValue,
          left = tp(iframe, cs.paddingLeft),
          top = tp(iframe, cs.paddingTop);

        coords.x += ifc.x + left - scroll.x;
        coords.y += ifc.y + top - scroll.y;
      }
    } else {
      coords = (domGeometry as any).position(target, true);
      coords.x += 10;
      coords.y += 10;
    }

    var self = this;
    var prevFocusNode = this._focusManager.get('prevNode');
    var curFocusNode = this._focusManager.get('curNode');
    var savedFocusNode = !curFocusNode || (dom.isDescendant(curFocusNode, this.domNode)) ? prevFocusNode : curFocusNode;

    function closeAndRestoreFocus() {
      // user has clicked on a menu or popup
      if (self.refocus && savedFocusNode) {
        savedFocusNode.focus();
      }
      pm.close(self);
    }

    pm.open({
      popup: this,
      x: coords.x,
      y: coords.y,
      onExecute: closeAndRestoreFocus,
      onCancel: closeAndRestoreFocus,
      orient: this.isLeftToRight() ? 'L' : 'R',
    });

    // Focus the menu even when opened by mouse, so that a click on blank area of screen will close it
    this.focus();
    if (!byKeyboard) {
      // But then (when opened by mouse), mark Menu as passive, so that the first item isn't highlighted.
      this.defer(function (this: any) {
        this._cleanUp(true);
      });
    }

    this._onBlur = function _menuOnBlur(this: any) {
      // mds: strict-mode explicit-caller inherited form for this raw instance override.
      this.inherited('_onBlur', _menuOnBlur, arguments);
      // Usually the parent closes the child widget but if this is a context
      // menu then there is no parent
      pm.close(this);
      // don't try to restore focus; user has clicked another part of the screen
    };
  },

  destroy: function () {
    array.forEach(this._bindings, function (this: any, b: any) {
      if (b) {
        this.unBindDomNode(b.node);
      }
    }, this);
    this.inherited(arguments);
  },
});
